import { humanizeActionName } from '@/right-panel/role/action-categories';

/**
 * Every string the access-denied surface renders, in one place so the page, the
 * preview variant and the palette's starved-role state cannot drift apart.
 *
 * Writing rules honoured here: say what happened and what to do about it, in the
 * interface's voice. Never apologize, never speculate about why, never promise an
 * outcome the app cannot deliver.
 */

export interface IDeniedCopyInput {
	/** Product-language name, e.g. 'Incidents'. */
	label: string;
	/** Ancestor titles, e.g. ['Situation Room']. */
	trail: string[];
	/** Codes that would open it — OR semantics. */
	missing: readonly string[];
	/** The signed-in user's roles. */
	roles: readonly string[];
	isKnown: boolean;
	/**
	 * Set for a team member: whose account they work in. Their access is a per-person grant list made in
	 * Team access, not a role, so "grant your role" advice would send them to the wrong place.
	 */
	teamOwner?: string | null;
}

export const breadcrumb = ({ trail, label }: Pick<IDeniedCopyInput, 'trail' | 'label'>) => [...trail, label].join(' › ');

export const headline = ({ label, isKnown }: Pick<IDeniedCopyInput, 'label' | 'isKnown'>) =>
	isKnown ? `You can't open ${label}` : "You can't open this page";

export const lede = ({ label, trail, missing, isKnown, teamOwner }: IDeniedCopyInput) => {
	if (teamOwner) {
		return isKnown
			? `${label} isn't part of your team access in ${teamOwner}'s account.`
			: `This address isn't a module in iElect, or it isn't part of your team access in ${teamOwner}'s account.`;
	}

	if (!isKnown) {
		return "This address isn't a module in iElect, or it needs a permission your roles don't have.";
	}

	const where = trail.length > 0 ? `${label} sits under ${trail.join(' › ')}. ` : '';

	if (missing.length === 1) {
		return `${where}Opening it needs the “${humanizeActionName(missing[0])}” permission, and none of your roles have it.`;
	}

	return `${where}Opening it needs any one of ${missing.length} permissions, and none of your roles have them.`;
};

/**
 * The paste-ready request.
 *
 * The re-login line is load-bearing and true: the permission set is materialized from
 * the JWT at sign-in, so a grant made now is invisible until the user signs out and
 * back in. Leaving it out is what produces the "they gave me access and it still
 * doesn't work" call.
 */
export const requestMessage = ({ label, trail, missing, roles, email, teamOwner }: IDeniedCopyInput & { email: string }) => {
	const destination = breadcrumb({ trail, label });
	const codes = missing.join(' or ');
	const who = roles.length > 0 ? roles.join(', ') : 'no roles assigned';

	if (teamOwner) {
		return [
			`Hi — I work in ${teamOwner}'s iElect account and need ${destination} added to my team access.`,
			`Permission code: ${codes || '(unknown)'}`,
			`My account: ${email || '(unknown)'}`,
			`${teamOwner} can ask for it from the Team page, or a Super Admin can change it under System Management › Team access.`,
			"I'll need to sign out and back in before it takes effect.",
		].join('\n');
	}

	return [
		`Hi — I need access to ${destination} in iElect.`,
		`Permission code: ${codes || '(unknown)'}`,
		`My account: ${email || '(unknown)'} (${who})`,
		'Grant it under System Management › Roles › <role> › Manage Actions.',
		"I'll need to sign out and back in before it takes effect.",
	].join('\n');
};

/** "Who can grant it" for a team member: their owner asks, a Super Admin changes Team access. */
export const teamGrantHint = (teamOwner: string) =>
	`${teamOwner} can ask for this from their Team page. A Super Admin changes it under System Management › Team access.`;

export const COPY = {
	whatBlock: "What you're opening",
	whoBlock: 'Who you are',
	missingBlock: "What's missing",
	grantBlock: 'Who can grant it',
	multiMissingHint: 'Any one of these would open it',
	grantHint: 'People with the Super Admin role can grant this from System Management › Roles › Manage Actions.',
	grantUnavailable: "We couldn't load who can grant this. Copy the request and send it to whoever manages your iElect accounts.",
	copyAction: 'Copy the request',
	copied: 'Request copied',
	copyHint: 'Paste this into WhatsApp, SMS or email — whichever gets an answer fastest.',
	noRoles: 'Your account has no roles assigned yet.',
	starvedTitle: "Your roles don't open any modules yet",
	starvedBody: 'Ask whoever manages your iElect accounts to grant your role access to the modules you need.',
} as const;
